import React, { useState } from 'react';
import clsx from 'clsx';
import { Typography } from '../../../_shared/components/Typography/Typography';
import { Card } from '../../../_shared/components/Card/Card';
import { Input } from '../../../_shared/components/Input/Input';
import { KpiFactsTableData } from '../../../_shared/types';
import { ReactComponent as InfoIcon } from '../../../_shared/assets/icons/info-icon.svg';
import { Button } from '../../../_shared/components/Button/Button';
import styles from './CardDataDesktop.module.scss';

interface Props {
  content: KpiFactsTableData[];
  sentKpis: Set<string>;
  onSend: (kpiId: string) => void;
  onDiscuss: () => void;
  onMethodologyClick?: (kpiId: string) => void;
}

export function CardDataDesktop({
  content: initialContent,
  sentKpis,
  onSend,
  onDiscuss,
  onMethodologyClick
}: Props) {
  const [data, setData] = useState(initialContent);

  const handleChange = (
    kpiId: string,
    responsibleId: string,
    period: string,
    rawValue: string
  ) => {
    const numValue = rawValue.trim() === '' ? null : Number(rawValue);
    setData((prev) =>
      prev.map((kpi) => {
        if (kpi.kpiId !== kpiId) return kpi;
        return {
          ...kpi,
          responsibles: kpi.responsibles.map((person) => {
            if (person.id !== responsibleId) return person;
            return {
              ...person,
              facts: person.facts.map((fact) => {
                if (fact.period !== period) return fact;
                return { ...fact, value: numValue };
              })
            };
          })
        };
      })
    );
  };

  return (
    <div className={styles.cards}>
      {data.map((kpi) => {
        const isSent = sentKpis.has(kpi.kpiId);

        return (
          <div key={kpi.kpiId} className={styles.card}>
            <div className={styles.head}>
              <Typography variant={'h4'} className={styles.kpiTitle}>
                {kpi.kpiId}. {kpi.kpiName}, {kpi.unit}
              </Typography>
              <button
                className={styles.methodLink}
                onClick={() => onMethodologyClick?.(kpi.kpiId)}
              >
                Методика расчета &nbsp; <InfoIcon className={styles.infoIcon} />
              </button>
            </div>

            <Card>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thNum} rowSpan={2}>
                      №
                    </th>
                    <th className={styles.thName} rowSpan={2}>
                      ФИО руководителя
                    </th>
                    <th className={styles.thPosition} rowSpan={2}>
                      Название должности
                    </th>
                    <th className={styles.thPlan} rowSpan={2}>
                      План {kpi.year}
                    </th>
                    <th className={styles.thSub}>Факт {kpi.year}, 6 мес</th>
                    <th className={styles.thSub}>Факт {kpi.year}, 9 мес</th>
                    <th className={styles.thSub}>Факт {kpi.year}, 12 мес</th>
                    {isSent && (
                      <>
                        <th className={styles.thSub}>Апелляция</th>
                        <th className={styles.thSub} />
                      </>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {kpi.responsibles.map((row, index) => (
                    <tr key={row.id} className={styles.row}>
                      <td className={styles.tdNum}>{index + 1}</td>
                      <td className={styles.tdName}>
                        <Typography variant={'h6'}>{row.fullName}</Typography>
                      </td>
                      <td className={styles.tdPosition}>
                        <Typography variant={'text'}>{row.position}</Typography>
                      </td>
                      <td className={styles.tdPlan}>
                        <Typography variant={'text'}>
                          {row.plan ?? '—'}
                        </Typography>
                      </td>

                      {row.facts.map((fact, fIndex) => (
                        <td
                          key={fIndex}
                          className={clsx(styles.tdFact, {
                            [styles.editing]: fact.isEditable && !isSent
                          })}
                        >
                          {fact.isEditable && !isSent ? (
                            <Input
                              label=""
                              value={fact.value?.toString() ?? ''}
                              onChange={(e) =>
                                handleChange(
                                  kpi.kpiId,
                                  row.id,
                                  fact.period,
                                  e.target.value
                                )
                              }
                              type="number"
                              className={styles.editInputWrapper}
                            />
                          ) : (
                            <Typography variant={'text'}>
                              {fact.value ?? '—'}
                            </Typography>
                          )}
                        </td>
                      ))}

                      {isSent && (
                        <>
                          <td className={styles.tdAppeal}>
                            {row.hasAppeal && (
                              <Typography
                                variant={'text'}
                                className={styles.appealText}
                              >
                                Есть
                              </Typography>
                            )}
                          </td>
                          <td className={styles.tdDiscuss}>
                            {row.hasAppeal && (
                              <button
                                type="button"
                                className={styles.discussBtn}
                                onClick={onDiscuss}
                              >
                                Обсудить
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              <Button
                variant={isSent ? 'disabled' : 'primary'}
                disabled={isSent}
                className={styles.button}
                onClick={() => !isSent && onSend(kpi.kpiId)}
              >
                {isSent
                  ? 'Отправлено на ознакомление'
                  : 'Отправить на ознакомление'}
              </Button>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
